import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { isEmpty } from 'lodash';

import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { WarmedatenService } from '@app/shared/services/api/warmedaten.service';
import { ErrorDialogComponent } from '@app/shared/dialog/error-dialog/error-dialog.component';
import { TermsAndConditionsDialogComponent } from '@app/shared/dialog/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { IErrorDialogComponent } from '@app/shared/models/dialog/error-dialog';
import { ITermsAndConditionsDialogComponent } from '@app/shared/models/dialog/terms-and-conditions-dialog';
import { ApplicationInsightEvent, HeatDataFileRequestStatus } from '@app/shared/enums';
import { HeatDataFileRequestInfo, HeatDownloadInfo } from '@app/shared/models/warmedaten/warmedaten-info';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { FederalStatesWithNDA } from '@app/shared/constants';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class WarmedatenDownloadService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public latestVersionDate$: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(null);
  public hasPendingFileRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isRefreshDisabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isDownloadDisabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public hasFileRequestError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public hasNDA$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private dialog: MatDialog,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private translateService: TranslateService,
    private warmedatenService: WarmedatenService,
    private overlayLoaderService: OverlayLoaderService,
    private blobStorageService: AzureBlobStorageService
  ) {}

  public hasRefreshPermissions(municipality: MapStoreMunicipality): void {
    this.hasFileRequestError$.next(false);
    this.isLoading$.next(true);
    this.overlayLoaderService.isLoading = true;
    this.hasNDA(municipality);

    this.warmedatenService
      .getHeatDataFileRequest(this.userMunicipalitiesService.currentMunicipality.key, '')
      .pipe(
        first(),
        switchMap((fileInfo: HeatDataFileRequestInfo[]) => {
          if (!isEmpty(fileInfo) && fileInfo[0].status === HeatDataFileRequestStatus.FAIL) {
            this.hasFileRequestError$.next(true);
            return this.warmedatenService
              .getHeatDataFileRequest(this.userMunicipalitiesService.currentMunicipality.key, HeatDataFileRequestStatus.COMPLETED)
              .pipe(first());
          }
          return of(fileInfo);
        })
      )
      .subscribe({
        next: (fileRequest: HeatDataFileRequestInfo[]) => {
          if (!isEmpty(fileRequest)) {
            if (fileRequest[0].status === HeatDataFileRequestStatus.PENDING) {
              this.updateStatuses(true, true, true);
            } else if (fileRequest[0].status === HeatDataFileRequestStatus.COMPLETED) {
              this.updateStatuses(false, false, false);
            } else if (fileRequest[0].status === HeatDataFileRequestStatus.FAIL) {
              this.updateStatuses(false, true, false);
            }
            this.latestVersionDate$.next(fileRequest[0].utcCompletionDate);
          } else {
            this.updateStatuses(false, true, true);
          }
          this.isLoading$.next(false);
          this.overlayLoaderService.isLoading = false;
        },
        error: () => {
          this.latestVersionDate$.next(null);
          this.isLoading$.next(false);
          this.overlayLoaderService.isLoading = false;
        },
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public download(permissionModule: string): void {
    this.openTermsAndConditionsDialog(permissionModule);
  }

  private openTermsAndConditionsDialog(permissionModule: string): void {
    const termsKey = permissionModule + (this.hasNDA$.value ? 'termsWithNda.' : 'terms.');
    const dialogRef = this.dialog.open<TermsAndConditionsDialogComponent, ITermsAndConditionsDialogComponent, boolean | undefined>(
      TermsAndConditionsDialogComponent,
      {
        data: this.getTermsAndConditionsDialogData(termsKey),
        panelClass: ['ep-generic-dialog'],
        disableClose: true,
      }
    );

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((confirmed?: boolean) => {
          if (!confirmed) {
            return of(null);
          }
          return this.warmedatenService.getHeatDataZip(this.userMunicipalitiesService.currentMunicipality.key);
        }),
        tap((info: HeatDownloadInfo | null) => {
          if (info) {
            this.handleDownloadResponse(info);
          }
        }),
        catchError((err: HttpErrorResponse | Error) => this.handleDownloadError(err, permissionModule)),
        first()
      )
      .subscribe();
  }

  private handleDownloadResponse(info: HeatDownloadInfo | null): void {
    if (!info?.downloadLink) {
      throw new Error('Heat data ZIP download link is missing.');
    }

    this.downloadHeatDataZipFile(info.downloadLink);
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.heatDataLegalConfirmation, this.getEventData());
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.heatDataDownloadNDA, this.getEventData());
  }

  private handleDownloadError(err: HttpErrorResponse | Error, permissionModule: string): Observable<never> {
    const errorKey = permissionModule + 'error.';
    this.dialog.open<ErrorDialogComponent, IErrorDialogComponent, unknown>(ErrorDialogComponent, {
      data: this.getErrorDialogData(errorKey),
      panelClass: ['ep-generic-dialog'],
      disableClose: true,
    });
    return throwError(() => err);
  }

  private hasNDA(municipality: MapStoreMunicipality): void {
    this.hasNDA$.next(FederalStatesWithNDA.some((ags) => municipality.key.substring(0, 2) === ags));
  }

  private downloadHeatDataZipFile(downloadLink: string): void {
    const link = document.createElement('a');
    link.setAttribute('href', downloadLink);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private get ndaPdfLink(): string {
    return this.blobStorageService.getBlobFilePath(`${environment.tenant}/downloadPdf/${environment.heatDataNdaPdfFileName}`);
  }

  private getEventData(): Record<string, string> {
    return {
      municipalityName: this.userMunicipalitiesService.currentMunicipality.name,
      municipalityKey: this.userMunicipalitiesService.currentMunicipality.key,
    };
  }

  private getTermsAndConditionsDialogData(termsKey: string): ITermsAndConditionsDialogComponent {
    return {
      title: this.translateService.instant(termsKey + 'title'),
      description: this.translateService.instant(termsKey + 'description', this.hasNDA$.value ? { ndaPdf: this.ndaPdfLink } : {}),
      terms: this.translateService.instant(termsKey + 'terms'),
      confirmText: this.translateService.instant(termsKey + 'confirmText'),
    };
  }

  private getErrorDialogData(errorKey: string): IErrorDialogComponent {
    return {
      title: this.translateService.instant(errorKey + 'title'),
      description: this.translateService.instant(errorKey + 'description'),
      buttonText: this.translateService.instant(errorKey + 'buttonText'),
    };
  }

  public refreshFileRequestData(permissionModule: string): void {
    this.warmedatenService
      .postHeatDataFileRequest(this.userMunicipalitiesService.currentMunicipality.key)
      .pipe(
        switchMap(() =>
          this.warmedatenService.getHeatDataFileRequest(this.userMunicipalitiesService.currentMunicipality.key, HeatDataFileRequestStatus.PENDING)
        ),
        catchError((err: HttpErrorResponse | Error) => this.handleDownloadError(err, permissionModule)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.updateStatuses(true, true, true);
        this.hasFileRequestError$.next(false);
      });
  }

  private updateStatuses(refreshButtonStatus: boolean, downloadButtonStatus: boolean, hasPendingStatus: boolean): void {
    this.isRefreshDisabled$.next(refreshButtonStatus);
    this.isDownloadDisabled$.next(downloadButtonStatus);
    this.hasPendingFileRequest$.next(hasPendingStatus);
  }
}
